import type { FactId } from './facts';
import type { AttemptView } from './mastery';

export interface SessionTotals {
  taskCount: number;
  totalAttempts: number;
  correctAttempts: number;
  wrongAttempts: number;
  assistedTasks: number;
  accuracy: number | null;
  totalAnswerMs: number;
  avgAnswerMs: number | null;
  avgCorrectAnswerMs: number | null;
}

/** Среднее пустой выборки — null, а не NaN: экран показывает «—». */
function mean(values: readonly number[]): number | null {
  return values.length === 0 ? null : values.reduce((a, b) => a + b, 0) / values.length;
}

/** Минимум сверткой с начальным значением: Math.min() без аргументов
 *  возвращает Infinity, а reduce без начального значения бросает. */
function min(values: readonly number[]): number | null {
  return values.reduce<number | null>((acc, v) => (acc === null || v < acc ? v : acc), null);
}

export function sessionTotals(
  attempts: readonly AttemptView[],
  taskCount: number,
  assistedTasks: number,
): SessionTotals {
  const correct = attempts.filter((a) => a.isCorrect);
  /** Суммируется только измеримое. Одна попытка с нечислом превращала
   *  среднее в `NaN`, и это `NaN` уезжало в строку занятия, откуда его уже
   *  никто не пересчитает: сводка не выводится из журнала заново. На экране
   *  итогов ребёнок видел бы «Средний ответ NaN». */
  const measured = attempts.filter((a) => Number.isFinite(a.answerMs) && a.answerMs > 0);
  const totalAnswerMs = measured.reduce((sum, a) => sum + a.answerMs, 0);
  return {
    taskCount,
    totalAttempts: attempts.length,
    correctAttempts: correct.length,
    wrongAttempts: attempts.length - correct.length,
    assistedTasks,
    accuracy: attempts.length === 0 ? null : (correct.length / attempts.length) * 100,
    totalAnswerMs,
    /** Средние считаются по ИЗМЕРИМЫМ, как и сумма. Отфильтровать одну
     *  сумму было недостаточно: `mean` по нефильтрованному списку возвращал
     *  `NaN` от одной испорченной строки, и «Средний ответ NaN» доезжало до
     *  экрана итогов и до строки занятия, откуда его уже никто не
     *  пересчитает. */
    avgAnswerMs: mean(measured.map((a) => a.answerMs)),
    avgCorrectAnswerMs: mean(measured.filter((a) => a.isCorrect).map((a) => a.answerMs)),
  };
}

export interface FactStats {
  appearances: number;
  correct: number;
  wrong: number;
  accuracy: number | null;
  avgAllMs: number | null;
  avgCorrectMs: number | null;
  bestCorrectMs: number | null;
  lastAtMs: number | null;
  /** Последние исходы по этому факту, от старых к новым (раздел 6.5). */
  recent: { isCorrect: boolean; answerMs: number; atMs: number }[];
  comparisons: { appearances: number; correct: number; wrong: number };
}

/** Сколько последних исходов показывать в статистике примера. */
const RECENT_LIMIT = 10;

/** Сравнения выделены отдельной строкой и не сливаются с остальными
 *  счётчиками: `9 × 8 > 9 × 7` решается по монотонности, не зная ни 72,
 *  ни 63, а время задания неделимо между двумя фактами (раздел 6.5). */
export function factStats(factId: FactId, attempts: readonly AttemptView[]): FactStats {
  const touching = attempts.filter((a) => a.factIds.includes(factId));
  const single = touching.filter((a) => a.exerciseType !== 'compare');
  const compare = touching.filter((a) => a.exerciseType === 'compare');
  const correct = single.filter((a) => a.isCorrect);

  return {
    appearances: single.length,
    correct: correct.length,
    wrong: single.length - correct.length,
    accuracy: single.length === 0 ? null : (correct.length / single.length) * 100,
    avgAllMs: mean(single.map((a) => a.answerMs)),
    avgCorrectMs: mean(correct.map((a) => a.answerMs)),
    bestCorrectMs: min(correct.map((a) => a.answerMs)),
    recent: single.slice(-RECENT_LIMIT).map((a) => ({
      isCorrect: a.isCorrect,
      answerMs: a.answerMs,
      atMs: a.atMs,
    })),
    lastAtMs: single.length === 0
      ? null
      : single.reduce((latest, a) => (a.order > latest.order ? a : latest)).atMs,
    comparisons: {
      appearances: compare.length,
      correct: compare.filter((a) => a.isCorrect).length,
      wrong: compare.filter((a) => !a.isCorrect).length,
    },
  };
}
